import { getBackendBase } from "../../trimble/coreFiles";

export type ExportJobStatus =
  | "queued"
  | "running"
  | "uploading"
  | "completed"
  | "failed"
  | "canceled";

export interface ExportJobProgress {
  phase: string;
  percent: number;
  message: string;
  updatedAt: string;
}

export interface ExportJobArtifactSummary {
  artifactId: string;
  kind?: string;
  filename?: string;
  contentType?: string;
  byteLength?: number;
  createdAt?: string;
  downloadPath?: string;
}

export interface ExportJobSummary {
  jobId: string;
  jobType: string;
  status: ExportJobStatus;
  requestedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  requestedBy?: {
    authProvider?: string;
    subject?: string;
    displayName?: string;
    notificationEmail?: string;
  };
  notificationEmail?: string;
  notification?: {
    recipient?: string;
    status?: string;
    transport?: string;
    lastAttemptAt?: string | null;
    completedAt?: string | null;
    subject?: string;
    message?: string;
    previewArtifactId?: string;
    previewDownloadPath?: string;
    operationId?: string;
  };
  filename?: string;
  progress?: ExportJobProgress;
  iTwin?: {
    iTwinId?: string;
    iModelId?: string;
    changesetId?: string;
  };
  trimble?: {
    projectId?: string;
    folderId?: string;
    folderPath?: string;
  };
  result?: {
    uploadedFiles?: string[];
    downloadUrl?: string | null;
    artifacts?: ExportJobArtifactSummary[];
    selectionSummary?: {
      selectedFeatureCount?: number;
      totalMatched3dElements?: number;
      totalMatched2dElements?: number;
      totalRelated2dGraphics?: number;
      totalRelated3dGraphics?: number;
      features?: Array<{
        featureId?: string;
        label?: string;
        definitionName?: string;
        featureKind?: string;
        matched3dElements?: number;
        matched2dElements?: number;
        related2dGraphics?: number;
        related3dGraphics?: number;
      }>;
    };
    geometryProbe?: {
      status?: string;
      message?: string;
      candidateMatched3dElementCount?: number;
      sampledElementCount?: number;
      skippedElementCount?: number;
      sampleLimit?: number;
      successfulElementCount?: number;
      failedElementCount?: number;
      elementsWithMeshes?: number;
      totalMeshCount?: number;
      totalByteLength?: number;
    };
    generatedIfc?: {
      status?: string;
      message?: string;
      candidateMatched3dElementCount?: number;
      candidateMatched2dElementCount?: number;
      exportedElementCount?: number;
      exported3dElementCount?: number;
      exported2dElementCount?: number;
      failedElementCount?: number;
      totalMeshCount?: number;
      totalCurvePrimitiveCount?: number;
      totalTriangleCount?: number;
      totalSourceByteLength?: number;
      filename?: string;
      artifactId?: string;
      downloadPath?: string;
      byteLength?: number;
    };
    trimbleUpload?: {
      folderId?: string;
      folderPath?: string;
      ifc?: {
        filename?: string;
        origin?: string;
        response?: unknown;
      };
    };
  };
  error?: string | null;
}

export interface ExportJobSelectedFeature {
  id: string;
  label: string;
  definitionName: string;
  featureKind: string;
}

export interface SubmitExportJobArgs {
  authorization: string;
  trimbleAccessToken: string;
  iTwinId: string;
  iModelId: string;
  changesetId?: string;
  selectedIds: string[];
  selectedFeatures?: ExportJobSelectedFeature[];
  filename: string;
  trimble: {
    projectId: string;
    folderId: string;
    folderPath?: string;
  };
  featureOptions?: Record<string, unknown>;
  notificationEmail?: string;
}

function requireBackendBase(): string {
  const backendBase = getBackendBase();
  if (!backendBase) {
    throw new Error("No backend base is configured for export jobs.");
  }
  return backendBase.replace(/\/$/, "");
}

export async function submitExportJob(args: SubmitExportJobArgs): Promise<ExportJobSummary> {
  const backendRoot = requireBackendBase();
  const response = await fetch(`${backendRoot}/jobs/export`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: args.authorization,
    },
    body: JSON.stringify({
      iTwinId: args.iTwinId,
      iModelId: args.iModelId,
      changesetId: args.changesetId ?? "",
      selectedIds: args.selectedIds,
      selectedFeatures: args.selectedFeatures ?? [],
      filename: args.filename,
      trimble: args.trimble,
      featureOptions: args.featureOptions ?? {},
      notificationEmail: args.notificationEmail ?? "",
      trimbleAccessToken: args.trimbleAccessToken ?? "",
    }),
  });

  const text = await response.text();
  let json: any = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!response.ok || !json?.job) {
    throw new Error(json?.error ?? text ?? `Export job submission failed (${response.status}).`);
  }

  return json.job as ExportJobSummary;
}

export async function fetchExportJob(jobId: string): Promise<ExportJobSummary> {
  const normalizedJobId = String(jobId ?? "").trim();
  if (!normalizedJobId) {
    throw new Error("Missing export job id.");
  }

  const backendRoot = requireBackendBase();
  const response = await fetch(`${backendRoot}/jobs/${encodeURIComponent(normalizedJobId)}`, {
    method: "GET",
  });

  const text = await response.text();
  let json: any = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!response.ok || !json?.job) {
    throw new Error(json?.error ?? text ?? `Export job lookup failed (${response.status}).`);
  }

  return json.job as ExportJobSummary;
}
